import { ReactElement, ReactNode } from 'react';
import { UIAvatar } from '../ui-avatar';
import { UIText } from '../ui-text';
import { NavLink, useLocation, useMatch } from 'react-router';
import styles from './ui-sub-nav.module.scss';

type UISubNavLinkProps = {
    text: string;
    link: string;
    additionalActiveLinkPaths?: string[];
};

type UISubNavProps = {
    title: string;
    subTitle?: string;
    /** Custom content rendered beneath the title (and subtitle), beside the avatar. */
    subContent?: ReactNode;
    img?: {
        img: string;
        alt: string;
    };
    rightItems?: ReactElement;
    links?: UISubNavLinkProps[];
};

export function UISubNav(props: UISubNavProps): ReactElement {
    const { title, subTitle, subContent, img, rightItems, links = [] } = props;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'space-between',
                    gap: 16
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'row',
                        alignItems: img ? 'center' : 'flex-start',
                        gap: 16
                    }}
                >
                    {img && <UIAvatar img={img.img} alt={img.alt} size={80} />}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4
                        }}
                    >
                        <UIText variant="h1">{title}</UIText>
                        {subTitle && (
                            <UIText type="default2">{subTitle}</UIText>
                        )}
                        {subContent}
                    </div>
                </div>
                {rightItems && <div>{rightItems}</div>}
            </div>
            {links.length > 0 && (
                <nav style={{ display: 'flex', gap: 16 }}>
                    {links.map((linkProps, index) => (
                        <LinkItem key={index} {...linkProps} />
                    ))}
                </nav>
            )}
        </div>
    );
}

function LinkItem(props: UISubNavLinkProps): ReactElement {
    const { text, link, additionalActiveLinkPaths } = props;
    const match = useMatch(link);
    const location = useLocation();

    return (
        <>
            {match && <div className={styles.selectedIndicator} />}
            <NavLink
                to={link}
                className={({ isActive }) => {
                    const active =
                        isActive ||
                        additionalActiveLinkPaths?.some((path) => {
                            return location.pathname === path;
                        });

                    return active
                        ? `${styles.itemContainer} ${styles.selected}`
                        : styles.itemContainer;
                }}
            >
                {text}
            </NavLink>
        </>
    );
}
